import React, { useRef, useEffect } from "react";
import Chart from "chart.js";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalysisMetrics } from "../../types";
import { SUCCESS_RATIO_COLORS } from "../../constants";
import { ChartContainer } from "../../styles";

/** Props for the SuccessRatioChart organism. */
interface SuccessRatioChartProps {
  metrics: TravelAnalysisMetrics | null;
  loading: boolean;
}

/**
 * Doughnut chart showing the ratio of successful trips vs trips with alerts.
 * Displays the success rate percentage as an HTML overlay in the center.
 */
const SuccessRatioChart: React.FC<SuccessRatioChartProps> = ({ metrics, loading }) => {

  const { t } = useTranslation();
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || loading || !metrics) return;

    const alertsCount = metrics.totalAnalyzed - metrics.successCount;

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: [
          `${t(TravelAnalysisMessages.success)} ${metrics.successCount.toLocaleString("pt-BR")}`,
          `${t(TravelAnalysisMessages.alerts)} ${alertsCount.toLocaleString("pt-BR")}`
        ],
        datasets: [{
          data: [metrics.successCount, alertsCount],
          backgroundColor: [SUCCESS_RATIO_COLORS.success, SUCCESS_RATIO_COLORS.alerts],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: {
          position: "bottom",
          labels: { usePointStyle: true, padding: 16 }
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets![tooltipItem.datasetIndex!];
              const value = dataset.data![tooltipItem.index!] as number;
              const total = (dataset.data as number[]).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

              return ` ${data.labels![tooltipItem.index!]}: ${pct}%`;
            }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [metrics, loading]);

  if (!loading && !metrics) {
    return (
      <ChartContainer>
        <div className="chart-title">{t(TravelAnalysisMessages.successRatioTitle)}</div>
        <div className="chart-empty">{t(TravelAnalysisMessages.noData)}</div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <div className="chart-title">{t(TravelAnalysisMessages.successRatioTitle)}</div>
      <div style={{ position: "relative", height: 280 }}>
        <canvas ref={canvasRef} />
        {metrics && (
          <div style={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none"
          }}>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#333" }}>
              {metrics.successRate.toFixed(1)}%
            </div>
            <div style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", letterSpacing: 1 }}>
              {t(TravelAnalysisMessages.efficiency)}
            </div>
          </div>
        )}
      </div>
    </ChartContainer>
  );
};

export default SuccessRatioChart;
